import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setEditedSeedHistory } from '../../actions/plan';
import { Unit } from '../../helpers/unit';
import { Form, DataTable } from '../widgets';

const LoadBulkEditRow = ({ unknownColumns, header }) => {
	const unit = Unit.getInstance();
	const initialState = { label: null, value: null };

	const { enableBulkEdit, selectedLoads, data, payload } = useSelector(state => state.plan.seedHistory);
	const { selectedPlanType } = useSelector(state => state.plan);
	const geoShapeList = useSelector(state => state.equipmentGroup.geoShapeList);
	const equipmentGroupList = useSelector(state => state.equipmentGroup.equipmentGroupList);

	const dispatch = useDispatch();
	const [edit, setEdit] = useState({
		crew: initialState,
		field: initialState,
		offload: initialState,
		storage: initialState,
		loadWeight: 0,
		seedVariety: '',
		seedSupplier: '',
		seedLot: '',
	});

	const showColumn = (key) => {
		if (enableBulkEdit || !unknownColumns || unknownColumns.length === 0) return true;
		return unknownColumns.indexOf(key) === -1;
	}


	const crewList = useMemo(() => {
		const key = "crew_uuid";
		const list = equipmentGroupList.filter(e => e.crew.name !== '' && e.alive && e.plan_type_id === selectedPlanType).map(e => e.crew);
		const crewList = [...new Map(list.map(item => [item[key], item])).values()];
		return [...crewList.map(e => ({ value: e.crew_uuid, label: e.name }))];
	}, [equipmentGroupList]);

	// const fieldOptions = useMemo(() => {
	// 	const fieldList = geoShapeList.filter(e => e.geo_region_type_id === 2 && e.active);
	// 	return fieldList.map(e => ({
	// 		value: e.geo_shape_uuid,
	// 		label: e.geo_shape_name
	// 	}));
	// }, [geoShapeList]);

	const { offloadOptions, offloadSiteList } = useMemo(() => {
		const offloadSiteList = geoShapeList.filter(e => e.geo_region_type_id === 4 && e.active);
		const offloadOptions = offloadSiteList.map(e => ({
			value: e.geo_shape_uuid,
			label: e.geo_shape_name
		}));
		return { offloadOptions, offloadSiteList };
	}, [geoShapeList]);

	const storageOptions = useMemo(() => {
		const selectedOffloadId = (edit.offload || {}).value;
		if (!selectedOffloadId) return [];
		const selectedOffloadInfo = offloadSiteList.find(e => e.geo_shape_uuid === selectedOffloadId);
		const storageList = Array.isArray(selectedOffloadInfo.geo_storage_locations) ? selectedOffloadInfo.geo_storage_locations : [];
		if (storageList.length === 0) {
			return [{
				value: 'default',
				label: 'Default'
			}];
		}
		return storageList.map(e => ({
			value: e.geo_storage_location_uuid,
			label: e.storage_location_name
		}));
	}, [offloadSiteList, edit.offload]);

	useEffect(() => {
		const storage = storageOptions.length > 0 ? storageOptions[0] : initialState;
		setEdit({ ...edit, storage: storage });
	}, [storageOptions]);

	useEffect(() => {
		const { crew, field, offload, storage, loadWeight, seedSupplier, seedVariety, seedLot } = edit;
		let loads = data && data.history ? data.history : [];

		loads = loads && loads.map((e, index) => {
			if (selectedLoads.indexOf(e.load_activity_uuid) > -1) return {
				...e,
				crew_name: crew && crew.label ? crew.label : payload.history[index].crew_name,
				crew_uuid: crew && crew.value ? crew.value : payload.history[index].crew_uuid,
				supplier: seedSupplier ? seedSupplier : payload.history[index].supplier,
				seed_lot: seedLot ? seedLot : payload.history[index].hasOwnProperty('seed_lot') ? payload.history[index].seed_lot : '',
				variety_name: seedVariety ? seedVariety : payload.history[index].variety_name,
				field_geo_shape_name: field && field.label ? field.label : payload.history[index].field_geo_shape_name,
				field_geo_shape_uuid: field && field.value ? field.value : payload.history[index].field_geo_shape_uuid,
				field_geo_region_name: field && field.regionName ? field.regionName : payload.history[index].field_geo_region_name,
				field_geo_region_uuid: field && field.regionId ? field.regionId : payload.history[index].field_geo_region_uuid,
				offload_geo_shape_name: offload && offload.label ? offload.label : payload.history[index].offload_geo_shape_name,
				offload_geo_shape_uuid: offload && offload.value ? offload.value : payload.history[index].offload_geo_shape_uuid,
				offload_geo_region_name: offload && offload.regionName ? offload.regionName : payload.history[index].offload_geo_region_name,
				offload_geo_region_uuid: offload && offload.regionId ? offload.regionId : payload.history[index].offload_geo_region_uuid,
				offload_geo_storage_location_name: storage && storage.label ? storage.label : payload.history[index].offload_geo_storage_location_name,
				offload_geo_storage_location_uuid: storage && storage.value ? storage.value : payload.history[index].offload_geo_storage_location_uuid,
				load_capacity: loadWeight ? unit.fromWeight(loadWeight, 2) : payload.history[index].load_capacity
			};
			else return e;
		});

		const updatedLoad = loads;
		dispatch(setEditedSeedHistory({ updatedLoad }));
	}, [edit, selectedLoads]);

	const headerKeyList = header.filter(it => showColumn(it.key) && it.isShow && (it.key !== "id") ? it.key : null).map(it => it.key);
	const tableValues = () => headerKeyList.map((headerKey, columnIndex) => {
		switch (headerKey) {
			case "crew_name": return (<DataTable.Cell key={columnIndex}>
				<Form.Select
					className="edit-row-input"
					id="crew"
					placeholder="Crew"
					name="crewId"
					onChange={(e) => {
						if (e) setEdit({ ...edit, crew: { value: e.value, label: e.label } });
						else setEdit({ ...edit, crew: e });
					}}
					value={(edit.crew || {}).value}
					options={crewList}
					sortType="asc" />
			</DataTable.Cell>)
			case "offload_geo_shape_name": return (<DataTable.Cell key={columnIndex}>
				<Form.Select
					className="edit-row-input"
					id="offload"
					placeholder="Offload Site"
					name="offloadId"
					onChange={(e) => {
						if (e) {
							const findShape = geoShapeList.find(item => item.geo_shape_uuid === e.value) || {};
							const offload = { ...e, regionId: findShape.geo_region_uuid, regionName: findShape.geo_region_name };
							setEdit({ ...edit, offload });
						} else {
							setEdit({ ...edit, offload: e });
						}
					}}
					value={(edit.offload || {}).value}
					options={offloadOptions}
					sortType="asc" />
			</DataTable.Cell>)
			case "offload_geo_storage_location_name": return (<DataTable.Cell key={columnIndex}>
				<Form.Select
					className="edit-row-input"
					id="storage"
					placeholder="Storage"
					name="storageId"
					onChange={(e) => setEdit({ ...edit, storage: e })}
					value={(edit.storage || {}).value}
					options={storageOptions} />
			</DataTable.Cell>)
			case "load_capacity": return (<DataTable.Cell key={columnIndex}>
				<Form.Control
					id="loadWeight"
					value={(edit.loadWeight || {}).value}
					onChange={(e) => setEdit({ ...edit, loadWeight: e.target.value })}
					min={0.1}
					max={1000000}
					type="number"
					placeholder="Load Weight"
					className="form-control edit-row-input"
					autoComplete="off"
					name="loadWeight" />
			</DataTable.Cell>)
			default: return (<DataTable.Cell key={columnIndex}></DataTable.Cell>)
		}
	});


	return (
		<DataTable.Row className="load-editable-row">
			<DataTable.Cell />
			{enableBulkEdit && <DataTable.Cell />}
			{tableValues()}
			{/* <DataTable.Cell>
				<Form.Select
					className="edit-row-input"
					id="field"
					placeholder="Field"
					name="fieldId"
					onChange={(e) => {
						if (e) {
							const findShape = geoShapeList.find(item => item.geo_shape_uuid === e.value) || {};
							const field = { ...e, regionId: findShape.geo_region_uuid, regionName: findShape.geo_region_name };
							setEdit({ ...edit, field });
						}
						else {
							setEdit({ ...edit, field: e });
						}
					}}
					value={(edit.field || {}).value}
					options={fieldOptions}
					sortType="asc" />
			</DataTable.Cell> */}
		</DataTable.Row>
	);
};
export default LoadBulkEditRow;